import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useEffect, useRef, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import EditPreAdmissionDetailsForm from './components/EditPreAdmissionDetailsForm';
import EditClientInformationForm from './components/EditClientInformationForm';
import EditReferrerInformationForm from './components/EditReferrerInformationForm';
import EditCaseStatusForm from './components/EditCaseStatusForm';
import EditBackGroundInformationForm from './components/EditBackGroundInformationForm';
import EditHealthConditionForm from './components/EditHealthConditionForm';
import EditResultsForm from './components/EditResultsForm';
import {
	useContinueEnquiryRecordDraftMutation,
	useCreateEnquiryRecordMutation,
	useEditEnquiryRecordMutation,
	useEnquiryRecordHasDraftQuery,
	useGetEnquiryRecordDraftQuery,
} from '@/api/forms-management/enquiryRecord';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { FormManagementStatus } from '@/api/enum/common.enum';

export default function CreateEnquiryRecordPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.create);
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list') ? 'admission-request-list' : 'member-list';
	const [createEnquiryRecord] = useCreateEnquiryRecordMutation();
	const { preAdmissionId, formType, admissionRequestId } = useParams();
	const showModal = useModal();
	const [editEnquiryRecord] = useEditEnquiryRecordMutation();

	const { data: details }: any = useGetEnquiryRecordDraftQuery(
		{ id: preAdmissionId || admissionRequestId || '', type: formType || '' },
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const clientInfoRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const preAdmissionRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const referrerInfoRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const caseStatusRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const resultsRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const backGroundInformationRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);
	const editHealthConditionRef = useRef<{
		getFormData: () => Promise<any>;
		validateForm: () => Promise<boolean>;
	}>(null);

	const handleSubmit = async (type: string) => {
		const results = await Promise.all([
			preAdmissionRef.current?.validateForm() || Promise.resolve(false),
			clientInfoRef.current?.validateForm() || Promise.resolve(false),
			referrerInfoRef.current?.validateForm() || Promise.resolve(false),
			caseStatusRef.current?.validateForm() || Promise.resolve(false),
			resultsRef.current?.validateForm() || Promise.resolve(false),
			backGroundInformationRef.current?.validateForm() ||
				Promise.resolve(false),
			editHealthConditionRef.current?.validateForm() || Promise.resolve(false),
		]);

		const validate = results.some((result) => result === false);
		if (type == 'verify') {
			enqueueSnackbar(
				validate
					? t('snackbar.fieldsVerifiedFalse')
					: t('snackbar.fieldsVerifiedTrue'),
				{
					variant: validate ? 'error' : 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			return;
		}
		if (validate) return;

		const { branchId, userId, enquiryDate, enquiryMethod } = {
			...(await preAdmissionRef.current?.getFormData()),
		};
		const {
			nameEn,
			nameZh,
			dateOfBirth,
			ageRange,
			sex,
			phone,
			maritalStatus,
			addressLanguage,
			region,
			district,
			subdistrict,
			estateCourtVillage,
			roadStreet,
			roadStreetNo,
			buildingBlock,
			buildingBlockSpecify,
			floor,
			flat,
		} = { ...(await clientInfoRef.current?.getFormData()) };
		const {
			approachSource,
			profileOfReferral,
			profileOfReferralSpecify,
			referralAgency,
			referralAgencySpecify,
			prefixOfReferrer,
			nameOfReferrer,
			positionOfReferrer,
			referrerContactNo,
			referrerFaxNo,
			caseNatureOfTheAgency,
			howTheCaseKnownToReferrer,
		} = { ...(await referrerInfoRef.current?.getFormData()) };
		const {
			suspectedMentalHealthProblems,
			defaultedPsychiatricFollowUp,
			defaultedPsychiatricFollowUpSince,
			awaitingPsychiatricFollowUp,
			hasRegularPsychiatricFollowUp,
			regularPsychiatricFollowUpSince,
			consentObtained,
			receivingReferralOrApplicationDate,
			enquiryRecordRemarks,
			membershipApplicationRemarks,
			faceSheetRemarks,
		} = {
			...(await caseStatusRef.current?.getFormData()),
		};
		const {
			resultHasContacted,
			resultCanContacted,
			resultCanContactedTarget,
			resultNeedDiscuss,
			resultNotSuitable,
			resultNotSuitableBecause,
			dateOfInformOic,
			dateOfConfirmationToReferrer,
		} = {
			...(await resultsRef.current?.getFormData()),
		};
		const { backgroundInformation } = {
			...(await backGroundInformationRef.current?.getFormData()),
		};
		const {
			cognitiveProblem,
			emotionalProblem,
			behavioralImpairments,
			interpersonalImpairments,
			riskElements,
		} = {
			...(await editHealthConditionRef.current?.getFormData()),
		};

		const formData = {
			branchId,
			userId,
			enquiryDate,
			enquiryMethod,
			nameEn,
			nameZh,
			dateOfBirth,
			ageRange,
			sex,
			phone,
			maritalStatus,
			addressLanguage,
			region,
			district,
			subdistrict,
			estateCourtVillage,
			roadStreet,
			roadStreetNo,
			buildingBlock,
			buildingBlockSpecify,
			floor,
			flat,
			approachSource,
			profileOfReferral,
			profileOfReferralSpecify,
			referralAgency,
			referralAgencySpecify,
			prefixOfReferrer,
			nameOfReferrer,
			positionOfReferrer,
			referrerContactNo,
			referrerFaxNo,
			caseNatureOfTheAgency,
			howTheCaseKnownToReferrer,
			suspectedMentalHealthProblems,
			defaultedPsychiatricFollowUp,
			defaultedPsychiatricFollowUpSince,
			awaitingPsychiatricFollowUp,
			hasRegularPsychiatricFollowUp,
			regularPsychiatricFollowUpSince,
			consentObtained,
			receivingReferralOrApplicationDate,
			enquiryRecordRemarks,
			membershipApplicationRemarks,
			faceSheetRemarks,
			resultHasContacted,
			resultCanContacted,
			resultCanContactedTarget,
			resultNeedDiscuss,
			resultNotSuitable,
			resultNotSuitableBecause,
			dateOfInformOic,
			dateOfConfirmationToReferrer,
			cognitiveProblem,
			emotionalProblem,
			behavioralImpairments,
			interpersonalImpairments,
			riskElements,
			backgroundInformation,
			id: details.id || '',
			formStatus:
				type == 'draft'
					? FormManagementStatus.draft
					: FormManagementStatus.submitted,
		};

		if (type == 'submit') {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={false}
					title={t('preAdmission.submitButtonTitle')}
					titleProps={{ variant: 'h6' }}
					description={
						<Typography variant='body1'>
							{t('preAdmission.submitButtonText')}
						</Typography>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.confirm')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					otherButtonText={t('button.saveDraft')}
					otherButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						saveApi({
							...formData,
							formStatus: FormManagementStatus.submitted,
						});
					}}
					onOtherConfirm={async () => {
						saveApi({ ...formData, formStatus: FormManagementStatus.draft });
					}}
				/>
			));
		} else if (type == 'draft') {
			saveApi({ ...formData, formStatus: FormManagementStatus.draft });
		}
	};

	const saveApi = async (formData: any) => {
		const response = details.id
			? await editEnquiryRecord(formData)
			: await createEnquiryRecord(formData);
		if ('data' in response) {
			enqueueSnackbar(
				formData.formStatus == FormManagementStatus.submitted
					? t('snackbar.fieldSynchronizationSuccess')
					: t('snackbar.draftEnquiryRecordSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			navigate(
				preAdmissionId
					? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
					: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
			);
		}
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Card sx={{ marginTop: 3 }}>
					<CardContent sx={{ padding: '24px' }}>
						<Typography variant='h4' marginBottom={'38px'}>
							{t('drawerListItems.newEnquiryRecord')}
						</Typography>
						<EditPreAdmissionDetailsForm
							ref={preAdmissionRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditClientInformationForm
							ref={clientInfoRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditReferrerInformationForm
							ref={referrerInfoRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditCaseStatusForm
							ref={caseStatusRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditBackGroundInformationForm
							ref={backGroundInformationRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditHealthConditionForm
							ref={editHealthConditionRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
						<EditResultsForm
							ref={resultsRef}
							type={type}
							setType={setType}
							details={details?.data}
						/>
					</CardContent>

					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button
							type='submit'
							onClick={() =>
								navigate(
									preAdmissionId
										? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
										: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`
								)
							}
						>
							{t('button.cancel')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('draft')}
							endIcon={<EditNoteOutlinedIcon />}
						>
							{t('button.saveDraft')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('verify')}
							endIcon={<TaskOutlinedIcon />}
						>
							{t('button.verifyFields')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={() => handleSubmit('submit')}
							endIcon={<CheckCircleOutlineIcon />}
						>
							{t('button.complete')}
						</Button>
					</CardActions>
				</Card>
			</Box>
		</>
	);
}
